import React, { useState, useCallback, useEffect, useRef } from "react";
import { collection, query, orderBy, where, limit, startAfter, getDocs, doc, getDoc } from "firebase/firestore";
import { firestoreProd } from "../../firebase";
import { getAuth } from "firebase/auth";
import { apiUrl } from "../../globalVariables";
import {
  FaBuilding,
  FaLock,
  FaSearch,
  FaSync,
  FaCopy,
  FaUserTie,
  FaFilter,
  FaDownload,
} from "react-icons/fa";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const PAGE_PASSWORD = "batman";

// ─── Plan classification (mirrors leadConversionCacheRefresh.js) ──────────────

function getPlan(subscription) {
  if (!subscription || subscription.status !== "active") return "FREE";
  const p = subscription.priceId || "";
  if (p.includes("enterprise")) return "ENTERPRISE";
  if (p.includes("growth")) return "GROWTH";
  if (p.includes("essentials")) return "ESSENTIALS";
  // Stripe price_* IDs are Growth subscriptions billed via Stripe
  if (p.startsWith("price_")) return "GROWTH";
  return "FREE";
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function copyToClipboard(text) {
  navigator.clipboard.writeText(text).then(() =>
    toast.success("Copied!", { autoClose: 1000 })
  );
}

function downloadCsv(rows, headers, filename) {
  const esc = (v) => {
    const s = String(v ?? "");
    return s.includes(",") || s.includes('"') || s.includes("\n")
      ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const csv = [headers.join(","), ...rows.map((r) => r.map(esc).join(","))].join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

function initials(name) {
  return (name || "?").split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toUpperCase();
}

// ─── Inline styles (scoped, light-theme) ─────────────────────────────────────

const S = {
  page: {
    padding: "28px 32px 48px",
    minHeight: "100vh",
    background: "#f8f9fa",
    fontFamily: "inherit",
    width: "100%",
  },
  // Gate
  gateWrap: {
    display: "flex", alignItems: "center", justifyContent: "center",
    minHeight: "70vh",
  },
  gateCard: {
    background: "#fff", borderRadius: 16,
    border: "1px solid #e5e7eb",
    boxShadow: "0 4px 24px rgba(0,0,0,0.07)",
    padding: "40px 48px", maxWidth: 380, width: "100%",
    display: "flex", flexDirection: "column", alignItems: "center", gap: 16,
  },
  gateLockWrap: {
    width: 56, height: 56, borderRadius: "50%",
    background: "linear-gradient(135deg,#7f7fd5,#a78bfa)",
    display: "flex", alignItems: "center", justifyContent: "center",
    fontSize: "1.3rem", color: "#fff",
    boxShadow: "0 6px 20px rgba(127,127,213,0.3)",
  },
  gateTitle: { fontSize: "1.2rem", fontWeight: 700, margin: 0, color: "#111827" },
  gateSubtitle: { color: "#6b7280", fontSize: "0.875rem", margin: 0, textAlign: "center" },
  gateForm: { display: "flex", flexDirection: "column", gap: 10, width: "100%" },
  gateInput: {
    padding: "10px 14px", borderRadius: 8, border: "1px solid #d1d5db",
    fontSize: "0.9rem", outline: "none", background: "#fff", color: "#111827",
  },
  gateError: { color: "#ef4444", fontSize: "0.82rem", textAlign: "center" },
  gateBtn: {
    padding: "10px", borderRadius: 8, border: "none",
    background: "linear-gradient(135deg,#7f7fd5,#a78bfa)",
    color: "#fff", fontSize: "0.9rem", fontWeight: 700,
    cursor: "pointer", boxShadow: "0 3px 10px rgba(127,127,213,0.3)",
  },
  // Header
  header: { marginBottom: 24 },
  h1: {
    fontSize: "1.5rem", fontWeight: 800, margin: "0 0 4px",
    display: "flex", alignItems: "center", gap: 10, color: "#111827",
  },
  h1Icon: {
    width: 34, height: 34, borderRadius: 9,
    background: "linear-gradient(135deg,#7f7fd5,#a78bfa)",
    display: "flex", alignItems: "center", justifyContent: "center",
    fontSize: "0.95rem", color: "#fff", flexShrink: 0,
  },
  subtitle: { color: "#6b7280", fontSize: "0.85rem", margin: 0 },
  // Tabs
  tabsWrap: {
    display: "flex", gap: 4, marginBottom: 24,
    borderBottom: "2px solid #e5e7eb",
  },
  tab: (active) => ({
    display: "flex", alignItems: "center", gap: 7,
    padding: "9px 20px", border: "none", borderRadius: "8px 8px 0 0",
    fontSize: "0.875rem", fontWeight: 600, cursor: "pointer",
    background: active ? "#fff" : "transparent",
    color: active ? "#7f7fd5" : "#6b7280",
    borderBottom: active ? "2px solid #7f7fd5" : "2px solid transparent",
    marginBottom: -2,
    transition: "all 0.15s",
  }),
  // Stats
  statsRow: { display: "flex", gap: 14, marginBottom: 22, flexWrap: "wrap" },
  statCard: (color) => ({
    background: "#fff", border: "1px solid #e5e7eb",
    borderRadius: 12, padding: "14px 20px", minWidth: 120,
    borderTop: `3px solid ${color}`,
    boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
  }),
  statLabel: { fontSize: "0.7rem", color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 4, fontWeight: 600 },
  statValue: (color) => ({ fontSize: "1.65rem", fontWeight: 800, color, lineHeight: 1 }),
  // Toolbar
  toolbar: { display: "flex", alignItems: "center", gap: 10, marginBottom: 14, flexWrap: "wrap" },
  searchWrap: { position: "relative", flex: 1, minWidth: 220 },
  searchInput: {
    width: "100%", padding: "9px 12px 9px 34px",
    borderRadius: 8, border: "1px solid #d1d5db",
    background: "#fff", color: "#111827", fontSize: "0.875rem",
    outline: "none", boxSizing: "border-box",
  },
  searchIcon: {
    position: "absolute", left: 11, top: "50%", transform: "translateY(-50%)",
    color: "#9ca3af", fontSize: "0.8rem", pointerEvents: "none",
  },
  btnPrimary: {
    display: "flex", alignItems: "center", gap: 6,
    padding: "9px 16px", borderRadius: 8, border: "none",
    background: "#7f7fd5", color: "#fff",
    fontSize: "0.85rem", fontWeight: 700, cursor: "pointer",
    whiteSpace: "nowrap", transition: "opacity 0.15s",
  },
  btnGhost: {
    display: "flex", alignItems: "center", gap: 6,
    padding: "9px 16px", borderRadius: 8,
    border: "1px solid #d1d5db", background: "#fff", color: "#374151",
    fontSize: "0.85rem", fontWeight: 600, cursor: "pointer",
    whiteSpace: "nowrap",
  },
  // Plan pills
  pillsRow: { display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center", marginBottom: 14 },
  pillLabel: { fontSize: "0.75rem", color: "#9ca3af", fontWeight: 600, display: "flex", alignItems: "center", gap: 5, marginRight: 2 },
  // Table
  tableWrap: {
    background: "#fff", border: "1px solid #e5e7eb",
    borderRadius: 12, overflow: "hidden",
    boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
  },
  table: { width: "100%", borderCollapse: "collapse", fontSize: "0.875rem" },
  thead: { background: "#f9fafb", borderBottom: "2px solid #e5e7eb" },
  th: {
    padding: "11px 14px", textAlign: "left", fontWeight: 700,
    color: "#374151", fontSize: "0.72rem", textTransform: "uppercase",
    letterSpacing: "0.06em", whiteSpace: "nowrap",
  },
  tr: (hover) => ({
    borderBottom: "1px solid #f3f4f6",
    background: hover ? "#f9fafb" : "#fff",
    transition: "background 0.1s",
  }),
  td: { padding: "12px 14px", verticalAlign: "middle", color: "#374151" },
  // Cells
  cellName: { display: "flex", alignItems: "center", gap: 10, fontWeight: 600, color: "#111827" },
  avatar: (bg) => ({
    width: 30, height: 30, borderRadius: 7, flexShrink: 0,
    display: "flex", alignItems: "center", justifyContent: "center",
    fontSize: "0.72rem", fontWeight: 800, color: "#fff",
    background: bg || "linear-gradient(135deg,#7f7fd5,#a78bfa)",
  }),
  cellMono: {
    display: "flex", alignItems: "center", gap: 6,
    fontFamily: "monospace", fontSize: "0.78rem", color: "#6b7280",
  },
  copyBtn: {
    background: "none", border: "none", color: "#d1d5db", cursor: "pointer",
    padding: "2px 4px", borderRadius: 4, fontSize: "0.72rem",
    transition: "color 0.15s",
  },
  // Empty
  empty: { textAlign: "center", padding: "60px 24px", color: "#9ca3af" },
  emptyIcon: { fontSize: "2.5rem", marginBottom: 12, opacity: 0.4 },
  // Notice
  notice: {
    background: "#fffbeb", border: "1px solid #fde68a",
    borderRadius: 8, padding: "10px 16px", marginBottom: 16,
    fontSize: "0.83rem", color: "#92400e",
  },
  resultCount: { fontSize: "0.8rem", color: "#9ca3af", marginBottom: 10 },
};

// Plan badge styles
const PLAN_STYLES = {
  FREE:        { background: "#f3f4f6", color: "#6b7280", border: "1px solid #e5e7eb" },
  ESSENTIALS:  { background: "#f0fdfa", color: "#0f766e", border: "1px solid #99f6e4" },
  GROWTH:      { background: "#eff6ff", color: "#1d4ed8", border: "1px solid #bfdbfe" },
  ENTERPRISE:  { background: "#f5f3ff", color: "#6d28d9", border: "1px solid #ddd6fe" },
};

const PLAN_COLORS = {
  FREE: "#9ca3af", ESSENTIALS: "#14b8a6", GROWTH: "#3b82f6", ENTERPRISE: "#7f7fd5",
};

function PlanBadge({ plan }) {
  const s = PLAN_STYLES[plan] || PLAN_STYLES.FREE;
  return (
    <span style={{
      ...s, display: "inline-flex", alignItems: "center", gap: 5,
      padding: "3px 10px", borderRadius: 20,
      fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.03em",
    }}>
      <span style={{ width: 6, height: 6, borderRadius: "50%", background: "currentColor", opacity: 0.7 }} />
      {plan}
    </span>
  );
}

const PLAN_FILTERS = [
  { key: "all",               label: "All" },
  { key: "FREE",              label: "Free" },
  { key: "ESSENTIALS",        label: "Essentials" },
  { key: "GROWTH",            label: "Growth" },
  { key: "ENTERPRISE",        label: "Enterprise" },
  { key: "ENTERPRISE_CLIENT", label: "Enterprise + Client" },
];

const FILTER_COLOR = {
  all:               "#374151",
  FREE:              PLAN_COLORS.FREE,
  ESSENTIALS:        PLAN_COLORS.ESSENTIALS,
  GROWTH:            PLAN_COLORS.GROWTH,
  ENTERPRISE:        PLAN_COLORS.ENTERPRISE,
  ENTERPRISE_CLIENT: "#7c3aed",
};

function PlanPills({ value, onChange, counts }) {
  return (
    <div style={S.pillsRow}>
      <span style={S.pillLabel}><FaFilter style={{ fontSize: "0.7rem" }} /> Filter</span>
      {PLAN_FILTERS.map(({ key, label }) => {
        const active = value === key;
        const color = FILTER_COLOR[key];
        return (
          <button
            key={key}
            onClick={() => onChange(key)}
            style={{
              padding: "5px 12px", borderRadius: 20, cursor: "pointer",
              fontSize: "0.78rem", fontWeight: 700, letterSpacing: "0.03em",
              border: active ? `1px solid ${color}` : "1px solid #e5e7eb",
              background: active ? (key === "all" ? "#f3f4f6" : `${color}18`) : "#fff",
              color: active ? color : "#9ca3af",
              transition: "all 0.15s",
            }}
          >
            {label}
            {counts && counts[key] !== undefined && (
              <span style={{ marginLeft: 5, opacity: 0.65 }}>({counts[key]})</span>
            )}
          </button>
        );
      })}
    </div>
  );
}

// ─── Password Gate ────────────────────────────────────────────────────────────

function PasswordGate({ onUnlock }) {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");

  const handleSubmit = (e) => {
    e.preventDefault();
    if (password === PAGE_PASSWORD) onUnlock();
    else { setError("Incorrect password."); setPassword(""); }
  };

  return (
    <div style={S.gateWrap}>
      <div style={S.gateCard}>
        <div style={S.gateLockWrap}><FaLock /></div>
        <h2 style={S.gateTitle}>Org Intelligence</h2>
        <p style={S.gateSubtitle}>Enter the password to access enterprise org data and contract processor reports.</p>
        <form onSubmit={handleSubmit} style={S.gateForm}>
          <input
            type="password" placeholder="Password"
            value={password}
            onChange={(e) => { setPassword(e.target.value); setError(""); }}
            style={S.gateInput}
            autoFocus
          />
          {error && <span style={S.gateError}>{error}</span>}
          <button type="submit" style={S.gateBtn}>Unlock</button>
        </form>
      </div>
    </div>
  );
}

// ─── Hoverable row ────────────────────────────────────────────────────────────

function TR({ children }) {
  const [hover, setHover] = useState(false);
  return (
    <tr
      style={S.tr(hover)}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      {children}
    </tr>
  );
}

const PAGE_SIZE = 50;
const USER_BATCH_SIZE = 10;

// priceId values per plan (mirrors backend seatTypes.js / leadConversionCacheRefresh.js)
const ENTERPRISE_PRICE_ID = "enterprise_monthly_price";
const GROWTH_PRICE_IDS = ["growth_monthly_price"];
const ESSENTIALS_PRICE_IDS = ["essentials_monthly_price"];

// Build Firestore constraints for a given filter.
// Returns an array of where() / orderBy() clauses to spread into query().
// ENTERPRISE_CLIENT is handled the same as ENTERPRISE at the DB level —
// the client-flag filtering is done in JS after resolving user docs.
function buildQueryConstraints(filter) {
  if (filter === "ENTERPRISE" || filter === "ENTERPRISE_CLIENT") {
    return [where("subscription.priceId", "==", ENTERPRISE_PRICE_ID), where("subscription.status", "==", "active"), orderBy("name")];
  }
  if (filter === "GROWTH") {
    // Growth includes both internal growth_* IDs and Stripe price_* IDs.
    // Firestore can't do "startsWith", so we query the known internal ID and
    // rely on the user scrolling to load Stripe-billed ones via "all" filter.
    return [where("subscription.priceId", "in", GROWTH_PRICE_IDS), where("subscription.status", "==", "active"), orderBy("name")];
  }
  if (filter === "ESSENTIALS") {
    return [where("subscription.priceId", "in", ESSENTIALS_PRICE_IDS), where("subscription.status", "==", "active"), orderBy("name")];
  }
  if (filter === "FREE") {
    // FREE = no active subscription. Query docs where status != "active" is
    // not straightforward; instead query where status == "none" or missing.
    // Simplest: fetch all and filter — but that's slow. Compromise: query
    // where subscription.status != "active" isn't supported directly, so
    // we use a broad fetch ordered by name and filter client-side for FREE.
    return [orderBy("name")];
  }
  // "all"
  return [orderBy("name")];
}

async function resolveClientFlags(orgs) {
  const enterpriseWithUser = orgs.filter((o) => o.plan === "ENTERPRISE" && o.defaultForUserId);
  const uniqueIds = [...new Set(enterpriseWithUser.map((o) => o.defaultForUserId))];
  const clientMap = new Map();
  for (let i = 0; i < uniqueIds.length; i += USER_BATCH_SIZE) {
    const batch = uniqueIds.slice(i, i + USER_BATCH_SIZE);
    const fetched = await Promise.all(batch.map((uid) => getDoc(doc(firestoreProd, "users", uid))));
    fetched.forEach((d) => {
      if (d.exists()) clientMap.set(d.id, d.data().createdAccountOnClient === true);
    });
  }
  return orgs.map((o) => ({
    ...o,
    createdAccountOnClient: o.defaultForUserId ? (clientMap.get(o.defaultForUserId) ?? false) : false,
  }));
}

function parseOrg(d) {
  const data = d.data();
  return {
    id: d.id,
    name: data.name || data.organizationName || "(no name)",
    status: data.subscription?.status || "none",
    priceId: data.subscription?.priceId || "",
    plan: getPlan(data.subscription),
    defaultForUserId: data.defaultForUserId || null,
    createdAccountOnClient: false,
  };
}

// ─── Orgs Tab ─────────────────────────────────────────────────────────────────

function OrgsTab({ onReady }) {
  const [orgs, setOrgs] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [search, setSearch] = useState("");
  const [planFilter, setPlanFilter] = useState("ENTERPRISE_CLIENT");
  const lastDocRef = useRef(null);
  const loadMoreRef = useRef(null);
  const isLoadingRef = useRef(false);
  const activeFilterRef = useRef(planFilter);
  // For ENTERPRISE_CLIENT: track how many client orgs we had before the last fetch
  // so we can auto-continue fetching if a page yielded zero new client orgs
  const clientCountBeforeFetchRef = useRef(0);

  const fetchPage = useCallback(async (filter, reset = false) => {
    if (isLoadingRef.current) return;
    isLoadingRef.current = true;
    setIsLoading(true);

    try {
      const constraints = buildQueryConstraints(filter);
      const cursor = reset ? null : lastDocRef.current;
      const q = query(
        collection(firestoreProd, "organizations"),
        ...constraints,
        limit(PAGE_SIZE),
        ...(cursor ? [startAfter(cursor)] : [])
      );

      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        setHasMore(false);
        isLoadingRef.current = false;
        setIsLoading(false);
        if (reset && onReady) onReady();
        return;
      }

      const dbHasMore = snapshot.docs.length >= PAGE_SIZE;
      lastDocRef.current = snapshot.docs[snapshot.docs.length - 1];
      if (!dbHasMore) setHasMore(false);

      let rawPage = snapshot.docs.map(parseOrg);

      if (filter === "FREE") rawPage = rawPage.filter((o) => o.plan === "FREE");

      const resolved = await resolveClientFlags(rawPage);

      if (activeFilterRef.current !== filter) {
        isLoadingRef.current = false;
        setIsLoading(false);
        return;
      }

      setOrgs((prev) => {
        const next = reset ? resolved : [...prev, ...resolved];

        // For ENTERPRISE_CLIENT: if this page added zero client orgs and there
        // are still more pages in the DB, kick off the next page automatically.
        if (filter === "ENTERPRISE_CLIENT" && dbHasMore) {
          const clientCountBefore = clientCountBeforeFetchRef.current;
          const clientCountAfter = next.filter((o) => o.createdAccountOnClient).length;
          if (clientCountAfter === clientCountBefore) {
            // Schedule next fetch outside of this setState call
            setTimeout(() => {
              if (activeFilterRef.current === filter) {
                isLoadingRef.current = false;
                fetchPage(filter, false);
              }
            }, 0);
            return next;
          }
          clientCountBeforeFetchRef.current = clientCountAfter;
        }

        return next;
      });

      if (reset && onReady) onReady();
    } catch (err) {
      console.error(err);
      toast.error(`Failed to load organizations: ${err?.message || err}`);
    } finally {
      // Only clear loading if we didn't schedule a continuation above
      // (the setTimeout path clears isLoadingRef itself before re-calling)
      if (isLoadingRef.current) {
        setIsLoading(false);
        isLoadingRef.current = false;
      }
    }
  }, [onReady]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset and re-fetch whenever the filter changes
  useEffect(() => {
    activeFilterRef.current = planFilter;
    lastDocRef.current = null;
    clientCountBeforeFetchRef.current = 0;
    setOrgs([]);
    setHasMore(true);
    fetchPage(planFilter, true);
  }, [planFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  // IntersectionObserver: auto-load next page on scroll
  useEffect(() => {
    const sentinel = loadMoreRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isLoadingRef.current) {
          clientCountBeforeFetchRef.current = orgs.filter((o) => o.createdAccountOnClient).length;
          fetchPage(activeFilterRef.current, false);
        }
      },
      { rootMargin: "300px" }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, fetchPage, orgs]);

  const handleRefresh = useCallback(() => {
    activeFilterRef.current = planFilter;
    lastDocRef.current = null;
    clientCountBeforeFetchRef.current = 0;
    setOrgs([]);
    setHasMore(true);
    fetchPage(planFilter, true);
  }, [planFilter, fetchPage]);

  const planCounts = orgs.reduce((acc, o) => {
    acc[o.plan] = (acc[o.plan] || 0) + 1;
    acc.all = (acc.all || 0) + 1;
    if (o.plan === "ENTERPRISE" && o.createdAccountOnClient) {
      acc.ENTERPRISE_CLIENT = (acc.ENTERPRISE_CLIENT || 0) + 1;
    }
    return acc;
  }, {});

  const filtered = orgs.filter((o) => {
    if (planFilter === "ENTERPRISE_CLIENT") return o.createdAccountOnClient;
    if (planFilter !== "all" && o.plan !== planFilter) return false;
    if (!search) return true;
    const t = search.toLowerCase();
    return o.name.toLowerCase().includes(t) || o.id.toLowerCase().includes(t) || o.priceId.toLowerCase().includes(t);
  });

  const hasAny = orgs.length > 0;

  return (
    <>
      {/* Toolbar — always visible */}
      <div style={S.toolbar}>
        <div style={S.searchWrap}>
          <FaSearch style={S.searchIcon} />
          <input
            type="text"
            placeholder="Search by name, org ID, or price ID…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={S.searchInput}
          />
        </div>
        <button style={{ ...S.btnPrimary, opacity: isLoading ? 0.6 : 1 }} onClick={handleRefresh} disabled={isLoading}>
          <FaSync style={isLoading ? { animation: "ep-spin 0.8s linear infinite" } : {}} />
          {isLoading ? "Loading…" : "Refresh"}
        </button>
        {hasAny && (
          <button style={S.btnGhost} onClick={() =>
            downloadCsv(
              filtered.map((o) => [o.name, o.id, o.plan, o.status, o.priceId, o.plan === "ENTERPRISE" ? (o.createdAccountOnClient ? "yes" : "no") : ""]),
              ["name", "orgId", "plan", "status", "priceId", "clientAccount"],
              `orgs-${planFilter.toLowerCase()}-${new Date().toISOString().slice(0, 10)}.csv`
            )
          }>
            <FaDownload /> Export CSV
          </button>
        )}
      </div>

      {/* Filters — always visible */}
      <PlanPills value={planFilter} onChange={setPlanFilter} counts={planCounts} />

      {/* Stats — shown once first page loaded */}
      {hasAny && (
        <div style={{ ...S.statsRow, marginTop: 4 }}>
          {[
            { label: "Loaded",            value: orgs.length.toLocaleString(),            color: "#7f7fd5" },
            { label: "Enterprise",        value: (planCounts.ENTERPRISE || 0),            color: PLAN_COLORS.ENTERPRISE },
            { label: "Enterprise+Client", value: (planCounts.ENTERPRISE_CLIENT || 0),     color: "#7c3aed" },
            { label: "Growth",            value: (planCounts.GROWTH || 0),                color: PLAN_COLORS.GROWTH },
            { label: "Essentials",        value: (planCounts.ESSENTIALS || 0),            color: PLAN_COLORS.ESSENTIALS },
            { label: "Free",              value: (planCounts.FREE || 0).toLocaleString(), color: PLAN_COLORS.FREE },
          ].map(({ label, value, color }) => (
            <div key={label} style={S.statCard(color)}>
              <div style={S.statLabel}>{label}</div>
              <div style={S.statValue(color)}>
                {value}
                {hasMore && <span style={{ fontSize: "0.65rem", color: "#d1d5db", marginLeft: 3 }}>+</span>}
              </div>
            </div>
          ))}
        </div>
      )}

      {hasAny && (
        <p style={S.resultCount}>
          Showing <strong>{filtered.length.toLocaleString()}</strong>
          {planFilter === "ENTERPRISE_CLIENT"
            ? hasMore
              ? <span style={{ color: "#9ca3af" }}> · scanning enterprise orgs for client accounts…</span>
              : null
            : hasMore
              ? <span style={{ color: "#9ca3af" }}> · scroll to load more</span>
              : null}
        </p>
      )}

      <div style={S.tableWrap}>
        {isLoading && !hasAny ? (
          <div style={S.empty}>
            <div style={{ ...S.emptyIcon, color: "#7f7fd5" }}><FaSync style={{ animation: "ep-spin 0.8s linear infinite" }} /></div>
            <p style={{ color: "#6b7280", marginTop: 12 }}>Loading organizations…</p>
          </div>
        ) : hasAny && filtered.length === 0 ? (
          <div style={S.empty}>
            <div style={S.emptyIcon}><FaBuilding /></div>
            <p>{search || planFilter !== "all" ? "No organizations match your filters yet — scroll to load more." : "No organizations found."}</p>
          </div>
        ) : hasAny ? (
          <>
            <table style={S.table}>
              <thead style={S.thead}>
                <tr>
                  <th style={{ ...S.th, width: 40 }}>#</th>
                  <th style={S.th}>Organization</th>
                  <th style={S.th}>Org ID</th>
                  <th style={S.th}>Plan</th>
                  <th style={S.th}>Client</th>
                  <th style={S.th}>Status</th>
                  <th style={S.th}>Price ID</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((org, idx) => (
                  <TR key={org.id}>
                    <td style={{ ...S.td, color: "#d1d5db", fontSize: "0.8rem", width: 40 }}>{idx + 1}</td>
                    <td style={S.td}>
                      <div style={S.cellName}>
                        <div style={S.avatar()}>{initials(org.name)}</div>
                        {org.name}
                      </div>
                    </td>
                    <td style={S.td}>
                      <div style={S.cellMono}>
                        {org.id}
                        <button style={S.copyBtn} onClick={() => copyToClipboard(org.id)} title="Copy ID">
                          <FaCopy />
                        </button>
                      </div>
                    </td>
                    <td style={S.td}><PlanBadge plan={org.plan} /></td>
                    <td style={S.td}>
                      {org.plan === "ENTERPRISE" ? (
                        org.createdAccountOnClient ? (
                          <span style={{
                            display: "inline-flex", alignItems: "center", gap: 5,
                            padding: "3px 10px", borderRadius: 20,
                            fontSize: "0.72rem", fontWeight: 700,
                            background: "#f0fdf4", color: "#16a34a", border: "1px solid #bbf7d0",
                          }}>
                            <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#4ade80", boxShadow: "0 0 5px rgba(74,222,128,0.6)" }} />
                            Active
                          </span>
                        ) : (
                          <span style={{
                            display: "inline-flex", alignItems: "center", gap: 5,
                            padding: "3px 10px", borderRadius: 20,
                            fontSize: "0.72rem", fontWeight: 700,
                            background: "#fafafa", color: "#9ca3af", border: "1px solid #e5e7eb",
                          }}>
                            No
                          </span>
                        )
                      ) : (
                        <span style={{ color: "#e5e7eb", fontSize: "0.8rem" }}>—</span>
                      )}
                    </td>
                    <td style={S.td}>
                      <span style={{
                        display: "inline-flex", alignItems: "center", gap: 6,
                        fontSize: "0.83rem",
                        color: org.status === "active" ? "#16a34a" : org.status === "none" ? "#9ca3af" : "#dc2626",
                      }}>
                        <span style={{
                          width: 7, height: 7, borderRadius: "50%",
                          background: org.status === "active" ? "#4ade80" : org.status === "none" ? "#d1d5db" : "#f87171",
                          boxShadow: org.status === "active" ? "0 0 5px rgba(74,222,128,0.6)" : "none",
                        }} />
                        {org.status === "none" ? "—" : org.status}
                      </span>
                    </td>
                    <td style={{ ...S.td, fontFamily: "monospace", fontSize: "0.76rem", color: "#9ca3af" }}>
                      {org.priceId || "—"}
                    </td>
                  </TR>
                ))}
              </tbody>
            </table>

            {/* Sentinel + footer */}
            <div ref={loadMoreRef} style={{ padding: "14px 20px", borderTop: "1px solid #f3f4f6", display: "flex", alignItems: "center", gap: 10, minHeight: 48 }}>
              {isLoading ? (
                <span style={{ fontSize: "0.8rem", color: "#9ca3af", display: "flex", alignItems: "center", gap: 6 }}>
                  <FaSync style={{ animation: "ep-spin 0.8s linear infinite", fontSize: "0.7rem" }} />
                  Loading more…
                </span>
              ) : hasMore ? (
                <button
                  style={{ ...S.btnGhost, fontSize: "0.82rem", padding: "7px 14px" }}
                  onClick={() => {
                    clientCountBeforeFetchRef.current = orgs.filter((o) => o.createdAccountOnClient).length;
                    fetchPage(activeFilterRef.current, false);
                  }}
                >
                  Load more
                </button>
              ) : (
                <span style={{ fontSize: "0.8rem", color: "#d1d5db" }}>All {filtered.length.toLocaleString()} organizations loaded</span>
              )}
            </div>
          </>
        ) : null}
      </div>

      <style>{`@keyframes ep-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </>
  );
}

// ─── Contract Processors Tab ──────────────────────────────────────────────────

function ContractProcessorsTab() {
  const [processors, setProcessors] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);
  const [search, setSearch] = useState("");

  const fetchProcessors = useCallback(async () => {
    setIsLoading(true);
    try {
      const userId = getAuth()?.currentUser?.uid;
      if (!userId) { toast.error("Admin user not found. Please refresh."); return; }
      const url = new URL(apiUrl + "/contract-processors");
      url.searchParams.append("userId", userId);
      const res = await fetch(url.toString());
      const json = await res.json();
      if (!json.success) throw new Error(json.reason || "Unknown error");
      setProcessors(json.data || []);
      setHasFetched(true);
    } catch (err) {
      console.error(err);
      toast.error(`Failed: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const filtered = processors.filter((p) => {
    const t = search.toLowerCase();
    return (
      (p.email || "").toLowerCase().includes(t) ||
      (p.fullName || "").toLowerCase().includes(t) ||
      (p.organizations || "").toLowerCase().includes(t) ||
      (p.nmlsId || "").toLowerCase().includes(t)
    );
  });

  const memberCount = processors.filter((p) => p.isOrgMember).length;

  return (
    <>
      <div style={S.notice}>
        ⚠️ This scans all Arive loans and downloads LOS objects from Storage — may take 30–120 seconds.
      </div>

      {hasFetched && (
        <div style={S.statsRow}>
          {[
            { label: "Unique Processors", value: processors.length, color: "#7f7fd5" },
            { label: "Org Members", value: memberCount, color: "#16a34a" },
            { label: "External", value: processors.length - memberCount, color: "#dc2626" },
          ].map(({ label, value, color }) => (
            <div key={label} style={S.statCard(color)}>
              <div style={S.statLabel}>{label}</div>
              <div style={S.statValue(color)}>{value}</div>
            </div>
          ))}
        </div>
      )}

      <div style={S.toolbar}>
        <div style={S.searchWrap}>
          <FaSearch style={S.searchIcon} />
          <input
            type="text"
            placeholder="Search by name, email, org, or NMLS ID…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={S.searchInput}
          />
        </div>
        <button style={{ ...S.btnPrimary, opacity: isLoading ? 0.6 : 1 }} onClick={fetchProcessors} disabled={isLoading}>
          <FaSync style={isLoading ? { animation: "ep-spin 0.8s linear infinite" } : {}} />
          {isLoading ? "Scanning…" : hasFetched ? "Refresh" : "Load Data"}
        </button>
        {hasFetched && (
          <button style={S.btnGhost} onClick={() =>
            downloadCsv(
              processors.map((p) => [p.email, p.fullName, p.firstName, p.lastName, p.phone, p.nmlsId, p.loanCount, p.organizations, p.isOrgMember]),
              ["email", "fullName", "firstName", "lastName", "phone", "nmlsId", "loanCount", "organizations", "isOrgMember"],
              `contract-processors-${new Date().toISOString().slice(0, 10)}.csv`
            )
          }>
            <FaDownload /> Export CSV
          </button>
        )}
      </div>

      {hasFetched && (
        <p style={S.resultCount}>
          Showing <strong>{filtered.length}</strong> of {processors.length} processors
        </p>
      )}

      <div style={S.tableWrap}>
        {isLoading && !hasFetched ? (
          <div style={S.empty}>
            <div style={{ ...S.emptyIcon, color: "#7f7fd5" }}><FaSync style={{ animation: "ep-spin 0.8s linear infinite" }} /></div>
            <p style={{ color: "#6b7280", marginTop: 12 }}>Scanning loans — this may take a minute…</p>
          </div>
        ) : !hasFetched ? (
          <div style={S.empty}>
            <div style={S.emptyIcon}><FaUserTie /></div>
            <p>Click "Load Data" to fetch contract processors from production.</p>
          </div>
        ) : filtered.length === 0 ? (
          <div style={S.empty}>
            <div style={S.emptyIcon}><FaUserTie /></div>
            <p>{search ? "No processors match your search." : "No contract processors found."}</p>
          </div>
        ) : (
          <table style={S.table}>
            <thead style={S.thead}>
              <tr>
                <th style={{ ...S.th, width: 40 }}>#</th>
                <th style={S.th}>Name</th>
                <th style={S.th}>Email</th>
                <th style={S.th}>Phone</th>
                <th style={S.th}>NMLS ID</th>
                <th style={S.th}>Loans</th>
                <th style={S.th}>Organizations</th>
                <th style={S.th}>Membership</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p, idx) => (
                <TR key={p.email}>
                  <td style={{ ...S.td, color: "#d1d5db", fontSize: "0.8rem", width: 40 }}>{idx + 1}</td>
                  <td style={S.td}>
                    <div style={S.cellName}>
                      <div style={S.avatar("linear-gradient(135deg,#0ea5e9,#6366f1)")}>{initials(p.fullName)}</div>
                      {p.fullName || "—"}
                    </div>
                  </td>
                  <td style={S.td}>
                    <div style={S.cellMono}>
                      {p.email}
                      <button style={S.copyBtn} onClick={() => copyToClipboard(p.email)} title="Copy">
                        <FaCopy />
                      </button>
                    </div>
                  </td>
                  <td style={{ ...S.td, color: "#6b7280", fontSize: "0.83rem" }}>{p.phone || "—"}</td>
                  <td style={{ ...S.td, color: "#6b7280", fontSize: "0.83rem" }}>{p.nmlsId || "—"}</td>
                  <td style={S.td}>
                    <span style={{ fontWeight: 700, color: "#7f7fd5" }}>{p.loanCount}</span>
                  </td>
                  <td style={{ ...S.td, color: "#6b7280", fontSize: "0.82rem", maxWidth: 260 }}>{p.organizations || "—"}</td>
                  <td style={S.td}>
                    <PlanBadge plan={p.isOrgMember ? "GROWTH" : "FREE"} />
                  </td>
                </TR>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

const EnterpriseOrgsPage = ({ onReady }) => {
  const [unlocked, setUnlocked] = useState(false);
  const [activeTab, setActiveTab] = useState("orgs");

  return (
    <div className="container-fluid EnterpriseOrgsPageContainer" style={S.page}>
      <ToastContainer position="bottom-right" theme="light" />

      {!unlocked ? (
        <PasswordGate onUnlock={() => setUnlocked(true)} />
      ) : (
          <>
            <div style={S.header}>
              <h1 style={S.h1}>
                <span style={S.h1Icon}><FaBuilding /></span>
                Org Intelligence
              </h1>
              <p style={S.subtitle}>Live production Firestore · All organizations &amp; contract processors</p>
            </div>

            <div style={S.tabsWrap}>
              <button style={S.tab(activeTab === "orgs")} onClick={() => setActiveTab("orgs")}>
                <FaBuilding /> Organizations
              </button>
              <button style={S.tab(activeTab === "processors")} onClick={() => setActiveTab("processors")}>
                <FaUserTie /> Contract Processors
              </button>
            </div>

            {activeTab === "orgs" && <OrgsTab onReady={onReady} />}
            {activeTab === "processors" && <ContractProcessorsTab />}
          </>
        )}
    </div>
  );
};

export default EnterpriseOrgsPage;
